<script lang="ts">
  import { QRCode } from "@castlenine/svelte-qrcode";
  import { userService } from "$lib/services/user.service";
  import type { ITotpCode } from "$lib/interfaces/ITotpCode";
  import { onMount } from "svelte";
  import { toasts } from "$lib/stores/toastStores";
  import Button from "$lib/elements/Button.svelte";
  import CodeInput from "$lib/elements/CodeInput.svelte";
  import {closeDialog} from "$lib/stores/dialogStores";
  import {translate} from "$lib/i18n";

  let loading = $state(true);
  let totp: ITotpCode = $state();
  let totpCode = "";
  let isValid = $state(false);

  onMount(() => {
    getCodeAndUrl();
  });

  async function getCodeAndUrl() {
    try {
      const response = await userService.postTotpCodeAndUrl();
      totp = response.data.data;
    } catch (err) {
      console.error(err);
      toasts.send("Failed to get TOTP code and URL", "error");
    } finally {
      loading = false;
    }
  }

  function copyToClipboard() {
    if (totp?.secretCode) {
      try {
        navigator.clipboard.writeText(totp.secretCode);
        toasts.send("Copied to clipboard", "info");
      } catch (err) {
        console.error(err);
        toasts.send("Failed to copy to clipboard", "error");
      }
    } else {
      toasts.send("Secret code not available", "error");
    }
  }

  async function setTotp() {
    try {
      const response = await userService.setTotp({ totpCode });
      if (response) {
        toasts.send("2FA enabled", "success");
        closeDialog("2FAModal");
      }
    } catch (err) {
      console.error(err);
      toasts.send("Failed to set TOTP code", "error");
    }
  }

  function handleOTPChange(event: any) {
    totpCode = event.detail.otpCode;
    isValid = event.detail.isValid;
  }
</script>

<div class="flex justify-center items-center flex-col my-1 text-white">
  <div class="flex justify-center items-center border-b border-b-primary-lightBlue mb-4 w-full">
    <span class="text-white text-xl pb-4">{$translate('2FA Connection')}</span>
  </div>
  <div class="space-y-3">
    <div class="bg-primary-dark p-3 rounded-2xl">
      <p class="text-sm text-gray-400">
        {$translate('Download and install')}
        <span class="text-primary-fire">
          <a
            href="https://support.google.com/accounts/answer/1066447?hl=en&co=GENIE.Platform%3DAndroid"
          >{$translate('Google Authenticator')}</a
          >
        </span>
      </p>
    </div>
    <div class="bg-primary-dark p-3 rounded-2xl flex flex-col items-center gap-3">
      <p class="text-sm text-gray-400">
        {$translate('Enter the secret key in your Google Authenticator app or scan the QR code.')}
      </p>
      {#if loading}
        {$translate('Loading...')}
      {:else}
        <QRCode
          padding={4}
          data="{totp?.url}"
          haveBackgroundRoundedEdges={true}
          width={140}
          height={140}
        />
        <div
          class="flex flex-col p-1.5 mt-2 text-left bg-primary-blue rounded-2xl text-gray-900 sm:text-sm focus:ring-primary-600 focus:border-primary-600 w-full"
        >
          <span class="text-white text-xs pl-2">{$translate('Your Secret Key:')}</span>
          <div class="flex justify-between items-center">
            <span class="text-gray-400 px-2 text-xs ">{totp.secretCode}</span>
            <div class="flex px-1 text-center items-center">
              <button onclick={copyToClipboard} type="button" class="flex items-center text-white bg-primary-fire shadow-lg font-medium rounded-full text-sm px-5 p-2 text-center">
                {$translate('Copy')}
              </button>
            </div>
          </div>
        </div>
      {/if}
    </div>
    <div class="bg-primary-dark p-3 rounded-2xl">
      <p class="text-gray-400 pl-1 text-sm">
        {$translate('Enter the one-time code from the Google Authenticator app below.')}
      </p>

      <div class="flex justify-center gap-2 my-4">
        <CodeInput on:change={handleOTPChange}/>
      </div>
    </div>

    <div class="flex justify-center">
      <div class:opacity-50={!isValid}>
        <Button variant="secondary" on:click={setTotp} disabled={!isValid}>
          {$translate('Enable 2FA')}
        </Button>
      </div>
    </div>
  </div>
</div>
